/**
 * Every 重点保障中心 write goes through here (#2658): one write per row at a
 * time, the row shows 「正在写入」 until the server answers, then either the
 * server's row plus where it has taken effect, or the server's error text.
 * After a success the Tokyo / Shanghai read-back is polled until Shanghai
 * holds the written version (or reports the row gone, after a delete).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CreateGuaranteedServiceRequest,
  GuaranteedScope,
  PatchGuaranteedServiceRequest,
} from '@nadoutong/contracts';
import {
  fetchGuaranteedPropagation,
  useCreateGuaranteedService,
  useDeleteGuaranteedService,
  usePatchGuaranteedService,
} from '../../api/guaranteedService.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { watchShanghai, setPropagationPollForTest } from './shanghaiWatch.ts';
import { describePatch, type CnConfirmation, type RowWriteStatus } from './writeStatus.ts';

export { setPropagationPollForTest };

/** How many deleted rows keep their status line under the panel caption. */
const REMOVED_KEEP = 5;

export interface WriteTarget {
  readonly id: string;
  readonly scope: GuaranteedScope;
}

export interface RemovedWrite {
  readonly id: string;
  readonly name: string;
  readonly status: RowWriteStatus;
}

export type WriteOutcome = { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * `quiet`: the caller shows the server's message inline (the 探测设置
 * drawer's footer), so a failure raises no toast — the toast sat on top of
 * that footer (staging rehearsal UI nit).
 */
export interface PatchOptions {
  readonly quiet?: boolean;
}

export interface GuaranteedWrites {
  readonly statusFor: (id: string) => RowWriteStatus | undefined;
  readonly removed: readonly RemovedWrite[];
  /** False when the row already has a write in flight (nothing is sent). */
  readonly patch: (target: WriteTarget, body: PatchGuaranteedServiceRequest, options?: PatchOptions) => Promise<WriteOutcome>;
  readonly create: (body: CreateGuaranteedServiceRequest) => Promise<WriteOutcome>;
  readonly remove: (target: WriteTarget & { readonly name: string }) => Promise<WriteOutcome>;
}

export function useGuaranteedWrites(): GuaranteedWrites {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const { mutateAsync: patchAsync } = usePatchGuaranteedService();
  const { mutateAsync: createAsync } = useCreateGuaranteedService();
  const { mutateAsync: deleteAsync } = useDeleteGuaranteedService();
  const [statuses, setStatuses] = useState<ReadonlyMap<string, RowWriteStatus>>(new Map());
  const [removed, setRemoved] = useState<readonly RemovedWrite[]>([]);
  const inFlight = useRef(new Set<string>());
  const pollers = useRef(new Map<string, AbortController>());
  // A write that resolves after the page is gone must not start a poll into
  // the already-cleared map (it would run for the full deadline).
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const active = pollers.current;
    return () => {
      mounted.current = false;
      for (const controller of active.values()) controller.abort();
      active.clear();
    };
  }, []);

  const setStatus = useCallback((id: string, status: RowWriteStatus): void => {
    setStatuses((current) => new Map(current).set(id, status));
    setRemoved((current) => current.map((row) => (row.id === id ? { ...row, status } : row)));
  }, []);

  const setCn = useCallback((ids: readonly string[], cn: CnConfirmation): void => {
    const targets = new Set(ids);
    const apply = (status: RowWriteStatus | undefined): RowWriteStatus | undefined => (
      status?.phase === 'saved' ? { ...status, cn } : status
    );
    setStatuses((current) => {
      const next = new Map(current);
      for (const id of targets) {
        const updated = apply(current.get(id));
        if (updated !== undefined) next.set(id, updated);
      }
      return next;
    });
    setRemoved((current) => current.map((row) => {
      const next = apply(row.status);
      return targets.has(row.id) && next !== undefined ? { ...row, status: next } : row;
    }));
  }, []);

  /**
   * Polls ONE id and applies its verdict to every id in `ids`. A multi-SKU
   * create commits all its rows in one transaction, so one representative
   * row stands for the batch (30 SKUs must not mean 30 polls every 2 s).
   */
  const startWatch = useCallback((
    ids: readonly string[],
    expectedUpdatedAt: string | null,
    goal: 'match' | 'missing',
  ): void => {
    watchShanghai({
      ids,
      expectedUpdatedAt,
      goal,
      read: fetchGuaranteedPropagation,
      fetchImpl,
      pollers: pollers.current,
      mounted,
      setCn,
    });
  }, [fetchImpl, setCn]);

  const fail = useCallback((id: string | null, error: unknown, quiet = false): WriteOutcome => {
    const message = coerceErrorMessage(error);
    if (id !== null) setStatus(id, { phase: 'error', message });
    if (!quiet) toast.error(message);
    return { ok: false, message };
  }, [setStatus, toast]);

  const patch = useCallback(async (
    target: WriteTarget,
    body: PatchGuaranteedServiceRequest,
    options: PatchOptions = {},
  ): Promise<WriteOutcome> => {
    if (inFlight.current.has(target.id)) {
      // Controls are disabled while saving, so this is a race (e.g. a dialog
      // confirmed as the row went busy). Say so instead of dropping it.
      const message = t('guaranteed.write.busy');
      toast.error(message);
      return { ok: false, message };
    }
    inFlight.current.add(target.id);
    pollers.current.get(target.id)?.abort();
    setStatus(target.id, { phase: 'saving', label: describePatch(t, body), pending: body });
    try {
      const response = await patchAsync({ id: target.id, body });
      setStatus(target.id, {
        phase: 'saved',
        op: 'write',
        scope: target.scope,
        cache: response.cache ?? null,
        updatedAt: response.item.updated_at,
        receivedAtMs: Date.now(),
        cn: { kind: 'waiting', last: null },
      });
      startWatch([target.id], response.item.updated_at, 'match');
      return { ok: true };
    } catch (error) {
      return fail(target.id, error, options.quiet === true);
    } finally {
      inFlight.current.delete(target.id);
    }
  }, [fail, patchAsync, setStatus, startWatch, t, toast]);

  const create = useCallback(async (body: CreateGuaranteedServiceRequest): Promise<WriteOutcome> => {
    try {
      const response = await createAsync(body);
      const receivedAtMs = Date.now();
      const latest = response.items.reduce<string | null>(
        (max, item) => (max === null || Date.parse(item.updated_at) > Date.parse(max) ? item.updated_at : max),
        null,
      );
      for (const item of response.items) {
        setStatus(item.id, {
          phase: 'saved',
          op: 'write',
          scope: item.scope,
          cache: response.cache ?? null,
          updatedAt: item.updated_at,
          receivedAtMs,
          cn: { kind: 'waiting', last: null },
        });
      }
      const representative = response.items.find((item) => item.updated_at === latest);
      if (representative !== undefined) {
        startWatch(
          [representative.id, ...response.items.filter((item) => item !== representative).map((item) => item.id)],
          representative.updated_at,
          'match',
        );
      }
      return { ok: true };
    } catch (error) {
      return fail(null, error);
    }
  }, [createAsync, fail, setStatus, startWatch]);

  const remove = useCallback(async (
    target: WriteTarget & { readonly name: string },
  ): Promise<WriteOutcome> => {
    if (inFlight.current.has(target.id)) {
      // Controls are disabled while saving, so this is a race (e.g. a dialog
      // confirmed as the row went busy). Say so instead of dropping it.
      const message = t('guaranteed.write.busy');
      toast.error(message);
      return { ok: false, message };
    }
    inFlight.current.add(target.id);
    pollers.current.get(target.id)?.abort();
    setStatus(target.id, { phase: 'saving', label: t('guaranteed.write.deleting'), pending: null });
    try {
      const response = await deleteAsync(target.id);
      const status: RowWriteStatus = {
        phase: 'saved',
        op: 'delete',
        scope: target.scope,
        cache: response.cache ?? null,
        updatedAt: null,
        receivedAtMs: Date.now(),
        cn: { kind: 'waiting', last: null },
      };
      setStatuses((current) => {
        const next = new Map(current);
        next.delete(target.id);
        return next;
      });
      setRemoved((current) => [
        { id: target.id, name: target.name, status },
        ...current.filter((row) => row.id !== target.id),
      ].slice(0, REMOVED_KEEP));
      startWatch([target.id], null, 'missing');
      return { ok: true };
    } catch (error) {
      return fail(target.id, error);
    } finally {
      inFlight.current.delete(target.id);
    }
  }, [deleteAsync, fail, setStatus, startWatch, t, toast]);

  const statusFor = useCallback((id: string) => statuses.get(id), [statuses]);

  return { statusFor, removed, patch, create, remove };
}
